import { ArrowRight, History } from 'lucide-react';
import {
  MAX_BATCH_MOVEMENTS,
  type BatchMovementDto,
  type MovementType,
  type Unit,
} from '@eombor/contracts';
import { Badge, Button, Dialog, EmptyState, Skeleton, cn, type BadgeTone } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { useBatchMovements } from '../model/stock.queries.js';
import { movementDocTypeText, movementTypeText, unitText } from '../../../shared/i18n/labels.js';

/**
 * Harakat turining rangi: kirim — ijobiy, chiqim — xatarli, ko'chirish —
 * ma'lumot, tuzatish — ogohlantirish. Ranglar tokenlardan keladi.
 */
const toneOf: Record<MovementType, BadgeTone> = {
  IN: 'success',
  OUT: 'danger',
  MOVE: 'info',
  ADJUST: 'warn',
};

/** `2026-08-11T14:40:00.000Z` → `11.08.2026 14:40` (mahalliy vaqt). */
export const formatMoment = (iso: string): string => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(at.getDate())}.${pad(at.getMonth() + 1)}.${at.getFullYear()} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
};

const MovementRow = ({ movement, unit }: { movement: BatchMovementDto; unit: Unit | null }) => (
  <li className="flex flex-col gap-1 border-b border-border py-2.5 last:border-b-0">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="flex items-center gap-2">
        <Badge tone={toneOf[movement.type]}>{movementTypeText(movement.type)}</Badge>
        <span className="tabular text-sm font-semibold text-text">
          {movement.qty}
          {unit === null ? '' : ` ${unitText(unit)}`}
        </span>
      </span>
      <span className="tabular text-xs text-text-faint">{formatMoment(movement.performedAt)}</span>
    </div>

    <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
      {/* Manzil: kirimda faqat qabul qiluvchi, chiqimda faqat beruvchi katak
          bo'ladi — jurnalning `CHECK` cheklovi shunday. */}
      {movement.fromCellCode === null ? null : (
        <span className="tabular">{movement.fromCellCode}</span>
      )}
      {movement.fromCellCode !== null && movement.toCellCode !== null ? (
        <ArrowRight size={12} aria-hidden />
      ) : null}
      {movement.toCellCode === null ? null : <span className="tabular">{movement.toCellCode}</span>}
      <span className="text-text-faint">{movementDocTypeText(movement.docType)}</span>
      {movement.reason === null || movement.reason === '' ? null : (
        <span className="min-w-0 truncate">{movement.reason}</span>
      )}
    </div>
  </li>
);

export interface BatchHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  /** `null` bo'lsa so'rov umuman ketmaydi. */
  batchId: string | null;
  batchNo: string;
  /** Miqdorlar yonidagi yorliq uchun — nomenklatura ma'lum bo'lsa. */
  unit?: Unit | null;
}

/**
 * Partiya harakat tarixi.
 *
 * Tartib SERVERDA (eng yangisi birinchi) — mijoz uni qayta tartiblamaydi:
 * bir tranzaksiyada yozilgan yozuvlarning vaqti bir xil bo'lishi mumkin va
 * u yerda `id` bo'yicha ikkinchi mezon ishlaydi. Jurnal o'zgarmas, shuning
 * uchun bu oynada hech qanday tahrir yo'q.
 */
export const BatchHistoryDialog = ({
  open,
  onClose,
  batchId,
  batchNo,
  unit = null,
}: BatchHistoryDialogProps) => {
  const movements = useBatchMovements(open ? batchId : null);
  const rows = movements.data ?? [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('stock.historyTitle', { batchNo })}
      size="lg"
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t('stock.close')}
        </Button>
      }
    >
      {movements.isPending ? (
        <Skeleton lines={5} />
      ) : movements.isError ? (
        <EmptyState
          title={t('stock.historyError')}
          description={
            movements.error instanceof ApiClientError ? movements.error.message : undefined
          }
          action={
            <Button variant="secondary" onClick={() => void movements.refetch()}>
              {t('stock.retry')}
            </Button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<History size={20} aria-hidden />}
          title={t('stock.historyEmpty')}
          description={t('stock.historyEmptyHint')}
        />
      ) : (
        <div className="flex flex-col gap-2">
          <ul className={cn('flex max-h-[26rem] flex-col overflow-y-auto')}>
            {rows.map((movement) => (
              <MovementRow key={movement.id} movement={movement} unit={unit} />
            ))}
          </ul>
          {rows.length >= MAX_BATCH_MOVEMENTS ? (
            <p className="text-xs text-text-faint">
              {t('stock.historyTruncated', { max: MAX_BATCH_MOVEMENTS })}
            </p>
          ) : null}
        </div>
      )}
    </Dialog>
  );
};
